import { Directive, ElementRef, HostListener, inject, Input, output } from '@angular/core';
import { NgControl } from '@angular/forms';

const NAVIGATION_KEYS = ['Tab', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

const DELETE_KEYS = ['Backspace', 'Delete'];

const TEXT_OPERATION_CODES = ['KeyA', 'KeyZ', 'KeyX', 'KeyC', 'KeyV'];

@Directive({
    selector: '[fiNumberOnly]',
})
export class NumberOnlyDirective {
    @Input() allowNegativeNumbers = true;
    @Input() allowDecimals = false;
    @Input() allowLeadingZero = true;
    public onBlur = output<Event>();

    private readonly elRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
    private readonly ngControl = inject(NgControl, {
        self: true,
        optional: true,
    });

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        if (this.isAllowedControlKey(event)) {
            return;
        }

        if (event.key.length > 1) {
            return;
        }

        const text = this.normalizeText(event.key);

        if (!this.canInsertText(text)) {
            event.preventDefault();
            return;
        }

        if (text !== event.key) {
            event.preventDefault();
            this.insertText(text);
        }
    }

    @HostListener('paste', ['$event'])
    onPaste(event: ClipboardEvent): void {
        const text = this.normalizeText(event.clipboardData?.getData('text/plain') ?? '');

        if (!this.canInsertText(text)) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        this.insertText(text);
    }

    @HostListener('drop', ['$event'])
    onDrop(event: DragEvent): void {
        const text = this.normalizeText(event.dataTransfer?.getData('text/plain') ?? '');

        if (!this.canInsertText(text)) {
            event.preventDefault();
            return;
        }

        event.preventDefault();
        this.insertText(text);
    }

    @HostListener('blur', ['$event'])
    handleBlur($event: Event): void {
        $event.preventDefault();
        let inputValue = this.elRef.nativeElement.value;
        inputValue = this.removeLeadingZero(inputValue);
        inputValue = this.roundValue(inputValue);
        inputValue = this.normalizeValue(inputValue);

        // Updates [(ngModel)] and triggers (ngModelChange).
        this.ngControl.control?.setValue(inputValue);

        this.onBlur.emit($event);
    }

    private removeLeadingZero(value: string): string {
        if (this.allowLeadingZero || !value?.startsWith('0') || value === '0') {
            return value;
        }

        return value.replace('0', '');
    }

    private roundValue(value: string): string {

        if (!value?.includes('.')) {
            return value;
        }

        const numberValue = +value;
        return numberValue.toLocaleString('en-US', {
            useGrouping: false,
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
        });
    }

    private normalizeValue(value: string): string {
        if (value?.startsWith('.')) {
            value = '0' + value;
        }

        if (value?.endsWith('.0')) {
            value = value.slice(0, -2);
        }

        if (value === '.' || isNaN(+value)) {
            value = '0';
        }

        return value;
    }

    private isAllowedControlKey(event: KeyboardEvent): boolean {
        return (
            NAVIGATION_KEYS.includes(event.key) ||
            DELETE_KEYS.includes(event.key) ||
            ((event.ctrlKey || event.metaKey) && TEXT_OPERATION_CODES.includes(event.code))
        );
    }

    private canInsertText(text: string): boolean {
        const inputEl = this.elRef.nativeElement;

        const start = inputEl.selectionStart ?? inputEl.value.length;
        const end = inputEl.selectionEnd ?? inputEl.value.length;

        const nextValue = inputEl.value.slice(0, start) + text + inputEl.value.slice(end);

        return this.isValidValue(nextValue);
    }

    private insertText(text: string): void {
        const inputEl = this.elRef.nativeElement;

        const start = inputEl.selectionStart ?? inputEl.value.length;
        const end = inputEl.selectionEnd ?? inputEl.value.length;

        inputEl.value = inputEl.value.slice(0, start) + text + inputEl.value.slice(end);

        const cursorPosition = start + text.length;

        inputEl.setSelectionRange(cursorPosition, cursorPosition);
        inputEl.dispatchEvent(new Event('input', { bubbles: true }));
    }

    private normalizeText(text: string): string {
        if (!this.allowDecimals) {
            return text;
        }

        return text.replace(/,/g, '.');
    }

    private isValidValue(value: string): boolean {
        const sign = this.allowNegativeNumbers ? '-?' : '';

        if (this.allowDecimals) {
            return new RegExp(`^${sign}(\\d+(\\.\\d*)?|\\.\\d*|\\d*)$`).test(value);
        }

        return new RegExp(`^${sign}\\d*$`).test(value);
    }
}
